// Ordering and filtering the crafting analysis.
//
// Outside the component for the same reason `recipeSort.ts` is: which row is
// at the top is exactly the kind of thing that goes wrong quietly, and it is
// the first thing a reader of this page believes.
import type { CraftableRow } from "../api/types";

/** What the table is ordered by.
 *
 *  `profit` prices every material at the market, the ones already in the bag
 *  included, and is the default: a Stem you own is a Stem you could have sold,
 *  so a ranking that treats carried material as free would put every recipe
 *  you happen to be hoarding at the top regardless of what it earns.
 *
 *  `gain` is the other reading — the same proceeds less only what is still to
 *  be bought — and it is what to sort by when the question is "I am going to
 *  craft something now, what". `outlay` is cheapest-to-finish first, and
 *  `covered` is closest-to-ready first.
 *
 *  Both money figures are struck against the standing bid (`proceeds`), not
 *  the ask, so this ordering ranks by what a craft would fetch rather than by
 *  what it might. Nothing here reads that field: `profit` and `gain` arrive
 *  already struck, and a row nobody is bidding on arrives null and falls to
 *  the bottom through `unknown` below, which is the one mechanism for it. */
export type CraftSort = "profit" | "gain" | "outlay" | "covered" | "item";

/** Which rows are shown. `ready` is nothing missing at all; `close` is that
 *  plus everything one material line short — the "one Coal short of the best
 *  margin on the server" band, which is the row this page exists to surface
 *  and the one a plain `ready` filter hides. */
export type CraftFilter = "all" | "close" | "ready";

export function shortLines(row: CraftableRow): number {
  return row.lines - row.covered;
}

export function matchesFilter(row: CraftableRow, filter: CraftFilter): boolean {
  if (filter === "ready") return row.ready;
  if (filter === "close") return shortLines(row) <= 1;
  return true;
}

/** Matches the crafted item and the recipe's own name, because a reader
 *  hunting "arrow" means either. */
export function filterCraftables(rows: CraftableRow[], needle: string): CraftableRow[] {
  const term = needle.trim().toLowerCase();
  if (!term) return rows;
  return rows.filter(
    (row) =>
      row.craft_item.toLowerCase().includes(term) || row.name.toLowerCase().includes(term),
  );
}

function byText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

/** A row whose money could not be worked out sorts last under every order.
 *
 *  The same rule the recipes page rests on, and for the same reason: an
 *  unknown is not a zero. A recipe missing its most expensive material, or one
 *  nobody is bidding on, would otherwise open this page as the best thing you
 *  could possibly make. */
function unknown(row: CraftableRow, sort: CraftSort): boolean {
  if (sort === "profit") return row.profit === null;
  if (sort === "gain") return row.gain === null;
  if (sort === "outlay") return row.outlay === null;
  return false;
}

export function sortCraftables(rows: CraftableRow[], sort: CraftSort): CraftableRow[] {
  // A copy: Array.prototype.sort is in place and these rows come straight out
  // of react-query's cache. Sort is stable in ES2019+, which keeps equal rows
  // from swapping between renders.
  return [...rows].sort((a, b) => {
    const aOut = unknown(a, sort);
    const bOut = unknown(b, sort);
    if (aOut !== bOut) return aOut ? 1 : -1;

    if (sort === "profit" && a.profit !== b.profit) {
      return (b.profit ?? 0) - (a.profit ?? 0);
    }
    if (sort === "gain" && a.gain !== b.gain) {
      return (b.gain ?? 0) - (a.gain ?? 0);
    }
    if (sort === "outlay" && a.outlay !== b.outlay) {
      // Cheapest to finish first. Zero — nothing left to buy — is the top of
      // this order, which is what makes it read as "what can I start now".
      return (a.outlay ?? 0) - (b.outlay ?? 0);
    }
    if (sort === "covered") {
      const short = shortLines(a) - shortLines(b);
      if (short !== 0) return short;
      // Among equally close rows, the one worth more. Ties on "2 lines short"
      // are the common case, and alphabetical there buries the good ones.
      const ap = a.profit;
      const bp = b.profit;
      if (ap === null || bp === null) {
        if (ap !== bp) return ap === null ? 1 : -1;
      } else if (ap !== bp) {
        return bp - ap;
      }
    }
    // The tie-break under every order, and the whole of the "item" one.
    return byText(a.craft_item, b.craft_item);
  });
}

/** What the missing column says: "12 Coal, 3 Suede", longest shortfall first,
 *  and never more than `limit` of them spelled out.
 *
 *  A cell is not a place to read six material names out of, and the tail is
 *  where the least interesting ones are — but the count of what was left out
 *  still has to be there, because "and 4 more" is the difference between a
 *  recipe that is nearly done and one that is not started. */
export function missingSummary(row: CraftableRow, limit = 2): string {
  if (row.missing.length === 0) return "";
  const shown = [...row.missing]
    .sort((a, b) => b.short - a.short)
    .slice(0, limit)
    .map((line) => `${line.short.toLocaleString()} ${line.name}`);
  const rest = row.missing.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} +${rest} more` : shown.join(", ");
}
